import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Users, FileText, ListChecks, MapPin, UserCog, Package, TrendingUp, AlertTriangle, Truck, DollarSign, ShoppingBag, Store, Crown, Settings, CreditCard, FileSpreadsheet, UtensilsCrossed } from "lucide-react";
import UpgradeBanner from "../components/UpgradeBanner";
import AvisoSemanalBanner from "../components/AvisoSemanalBanner";
import PreVendasBanner from "../components/PreVendasBanner";
import PreVendasAntecipadaBanner from "../components/PreVendasAntecipadaBanner";

const API = `${window.location.origin}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.tipo === 'admin' || !user.tipo;
  const isSecretario = user.tipo === 'secretario';
  const isVendedor = user.tipo === 'vendedor';
  const isSuperAdmin = !!user.super_admin;
  const permissoes = user.permissoes || {};
  const [pedidosPendentes, setPedidosPendentes] = useState(0);
  const [dividasSemFoto, setDividasSemFoto] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/loja/pedidos-pendentes`);
        setPedidosPendentes(Array.isArray(data) ? data.length : 0);
      } catch {}
      try {
        const { data } = await axios.get(`${API}/promissorias/sem-foto/count`);
        setDividasSemFoto(data?.count || 0);
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const menuButtons = [
    { path: '/clientes', label: 'Clientes', icon: Users, color: 'bg-blue-500' },
    { path: '/nova-promissoria', label: 'Nova Promissória', icon: FileText, color: 'bg-green-500' },
    ...((isAdmin || (isSecretario && permissoes.acessar_dividas) || isVendedor) ? [{ path: '/dividas', label: 'Dívidas', icon: ListChecks, color: 'bg-amber-500', badge: dividasSemFoto }] : []),
    { path: '/controle-rotas', label: 'Controle de Rotas', icon: MapPin, color: 'bg-teal-500' },
    { path: '/pedidos-loja', label: 'Pedidos Loja', icon: ShoppingBag, color: 'bg-emerald-500', badge: pedidosPendentes },
    ...((isAdmin || (isSecretario && permissoes.acessar_fluxo_caixa)) ? [{ path: '/fluxo-caixa', label: 'Gestão Financeira', icon: DollarSign, color: 'bg-purple-500' }] : []),
    ...(isAdmin ? [{ path: '/acessos', label: 'Acessos', icon: UserCog, color: 'bg-slate-600' }] : []),
    ...((isAdmin || isSecretario) ? [{ path: '/produtos', label: 'Catálogo', icon: Package, color: 'bg-orange-500' }] : []),
    { path: '/estoque-carro', label: 'Estoque Carro', icon: Truck, color: 'bg-indigo-500' },
    ...((isAdmin || isSecretario) ? [{ path: '/movimentacoes', label: 'Movimentações', icon: TrendingUp, color: 'bg-rose-500' }] : []),
    ...(isAdmin ? [{ path: '/configuracoes-loja', label: 'Config. da Loja', icon: Settings, color: 'bg-sky-600' }] : []),
    ...(isAdmin ? [{ path: '/assinatura', label: 'Minha Assinatura', icon: CreditCard, color: 'bg-emerald-600' }] : []),
    ...(isAdmin ? [{ path: '/planos', label: 'Planos', icon: Crown, color: 'bg-gradient-to-br from-amber-400 to-orange-500', highlight: true }] : []),
    ...(isSuperAdmin ? [{ path: '/gestao-assinantes', label: 'Gestão de Assinantes', icon: Crown, color: 'bg-amber-500', highlight: true }] : []),
    ...(isSuperAdmin ? [{ path: '/cotacoes/login', label: 'CD Cotações', icon: FileSpreadsheet, color: 'bg-emerald-600', highlight: true }] : []),
    ...(isSuperAdmin ? [{ path: '/vaipede/login', label: 'VAI PEDE', icon: UtensilsCrossed, color: 'bg-red-600', highlight: true, themeRed: true }] : []),
  ];

  return (
    <div data-testid="home-page">
      <UpgradeBanner />
      <PreVendasBanner />
      <PreVendasAntecipadaBanner />
      <AvisoSemanalBanner />
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Work Sans'}}>
          Olá, {user.nome || user.nome_loja || 'Usuário'}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">Selecione uma opção para começar</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
        {menuButtons.map((item) => {
          const Icon = item.icon;
          const hasBadge = typeof item.badge === 'number' && item.badge > 0;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`menu-btn-${item.path.replace('/', '')}`}
              className={`relative flex flex-col items-center justify-center aspect-square rounded-2xl border shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-150 p-3 gap-3 ${item.themeRed ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-400 ring-2 ring-red-300/50' : (item.highlight ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-400 ring-2 ring-amber-300/50' : 'bg-white border-gray-200')}`}
            >
              {hasBadge && (
                <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] px-1 flex items-center justify-center animate-pulse" data-testid={`badge-${item.path.replace('/', '')}`}>
                  {item.badge}
                </span>
              )}
              <Icon className={`h-10 w-10 ${item.themeRed ? 'text-red-600' : (item.highlight ? 'text-amber-600' : 'text-blue-500')}`} />
              <span className={`text-xs font-medium text-center leading-tight ${item.themeRed ? 'text-red-800 font-bold' : (item.highlight ? 'text-amber-800 font-bold' : 'text-gray-700')}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
